import React, { useState, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown, Filter } from 'lucide-react';
import { format } from 'date-fns';
import './InvoiceTable.css';

const InvoiceTable = ({ 
  invoices, 
  selectedInvoice, 
  onSelectInvoice, 
  filters, 
  onFilterChange,
  totalCount,
  loading 
}) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    
    const timeout = setTimeout(() => {
      onFilterChange({ ...filters, search: searchValue, offset: 0 });
    }, 300);
    
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchValue]);

  const handleSort = (field) => {
    const newOrder = filters.sortBy === field && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    onFilterChange({ ...filters, sortBy: field, sortOrder: newOrder, offset: 0 });
  };

  const handleStatusFilter = (status) => {
    onFilterChange({ ...filters, status, offset: 0 });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getSortIcon = (field) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  return (
    <div className="invoice-table-container">
      <div className="table-header">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${!filters.status ? 'active' : ''}`}
            onClick={() => handleStatusFilter('')}
          >
            All
          </button>
          <button 
            className={`filter-btn status-processed ${filters.status === 'processed' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('processed')}
          >
            Processed
          </button>
          <button 
            className={`filter-btn status-processed_with_warnings ${filters.status === 'processed_with_warnings' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('processed_with_warnings')}
          >
            Warnings
          </button>
          <button 
            className={`filter-btn status-manual_review_required ${filters.status === 'manual_review_required' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('manual_review_required')}
          >
            Review Required
          </button>
          <button 
            className={`filter-btn status-needs_verification ${filters.status === 'needs_verification' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('needs_verification')}
          >
            Needs Verification
          </button>
        </div>
      </div>

      <div className="table-info">
        Showing {invoices.length} of {totalCount} invoices
      </div>

      {loading ? (
        <div className="loading">Loading invoices...</div>
      ) : (
        <div className="table-wrapper">
          <table className="invoice-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('invoice_number')}>
                  Invoice # {getSortIcon('invoice_number')}
                </th>
                <th onClick={() => handleSort('invoice_date')}>
                  Date {getSortIcon('invoice_date')}
                </th>
                <th onClick={() => handleSort('supplier')}>
                  Supplier {getSortIcon('supplier')}
                </th>
                <th onClick={() => handleSort('total')}>
                  Total {getSortIcon('total')}
                </th>
                <th onClick={() => handleSort('status')}>
                  Status {getSortIcon('status')}
                </th>
                <th>Items</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, index) => (
                <tr
                  key={invoice.id}
                  className={selectedInvoice?.id === invoice.id ? 'selected' : ''}
                  onClick={() => onSelectInvoice(invoice, index)}
                >
                  <td>{invoice.invoice_number || 'N/A'}</td>
                  <td>
                    {invoice.invoice_date 
                      ? format(new Date(invoice.invoice_date), 'dd.MM.yyyy')
                      : 'N/A'}
                  </td>
                  <td>{invoice.supplier_name || 'Unknown'}</td>
                  <td>{formatCurrency(invoice.total, invoice.currency)}</td>
                  <td>
                    <span className={`status-badge status-${invoice.status || 'unknown'}`}>
                      {invoice.status || 'unknown'}
                    </span>
                  </td>
                  <td className="text-center">{invoice.item_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-pagination">
        <button 
          className="button button-secondary"
          disabled={filters.offset === 0}
          onClick={() => onFilterChange({ ...filters, offset: Math.max(0, filters.offset - filters.limit) })}
        >
          Previous
        </button>
        <span>
          Page {Math.floor(filters.offset / filters.limit) + 1} of {Math.ceil(totalCount / filters.limit)}
        </span>
        <button 
          className="button button-secondary"
          disabled={filters.offset + filters.limit >= totalCount}
          onClick={() => onFilterChange({ ...filters, offset: filters.offset + filters.limit })}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default InvoiceTable;